import React, { useEffect, useRef, useState } from "react";
import {
  LuChevronRight,
  LuExternalLink,
  LuPrinter,
  LuShare2,
} from "react-icons/lu";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  getPdfFile,
  updatePdfFile,
} from "../../features/settings/settingsSlice";
import { GET } from "../../services/axios.service";
import { openDownload } from "../../services/download.service";
import {
  clearPrintView,
  isPrintViewReady,
  openPrintWindow,
  preparePrintView,
} from "../../services/printWindow";
import { toastStyles } from "../../static/styles";
import { Dialog, DialogContent, DialogHeader } from "../ui/Dialog";
import cx from "../ui/cx";
import { dialogTitleClass } from "../ui/styles";

type LoadedPdf = {
  name: string;
  content: string;
  file: File;
};

type PrintView = {
  name: string;
  status: "preparing" | "ready" | "error";
};

const tileClass =
  "flex h-24 flex-col items-center justify-center gap-2 rounded-lg text-base font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50";
const printTileClass = `${tileClass} bg-[#113F67] text-white hover:bg-[#0d2f4f]`;
const outlineTileClass = `${tileClass} border-2 border-gray-200 bg-white text-[#113F67] hover:border-[#113F67]`;

const sheetClass =
  "font-inter max-sm:inset-x-0 max-sm:bottom-0 max-sm:top-auto max-sm:max-w-none max-sm:translate-x-0 max-sm:translate-y-0 max-sm:gap-3 max-sm:rounded-t-2xl max-sm:border-x-0 max-sm:border-b-0 max-sm:px-4 max-sm:pt-5 max-sm:pb-[max(1rem,env(safe-area-inset-bottom))] max-sm:max-h-[85dvh] max-sm:overflow-y-auto max-sm:overscroll-contain max-sm:motion-safe:animate-sheet-in";

const rowClass =
  "flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50";
const printRowClass = `${rowClass} bg-[#113F67] text-white active:bg-[#0d2f4f]`;
const outlineRowClass = `${rowClass} border-2 border-gray-200 bg-white text-[#113F67] active:border-[#113F67] active:bg-gray-50`;
const rowIconClass =
  "flex h-11 w-11 shrink-0 items-center justify-center rounded-full";
const closeButtonClass =
  "h-12 w-full rounded-xl border-2 border-gray-200 bg-white text-base font-semibold text-gray-600 transition-colors active:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 sm:hidden";

const errorMessage = "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.";

const isMobileDevice = (): boolean => {
  const userAgent = navigator.userAgent || "";

  if ((navigator as any).userAgentData?.mobile) return true;
  if (/Android|iPhone|iPad|iPod/i.test(userAgent)) return true;

  return /Macintosh/i.test(userAgent) && navigator.maxTouchPoints > 1;
};

// Phones get the print view as soon as the PDF loads, because iOS opens the print window only from the tap itself.
// Desktops make it on the first Print press.
const mobileDevice = isMobileDevice();

const PdfActions = () => {
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const pdfFile = useAppSelector(getPdfFile);

  const [pdf, setPdf] = useState<LoadedPdf | null>(null);
  const [printingNames, setPrintingNames] = useState<string[]>([]);
  const [printView, setPrintView] = useState<PrintView | null>(null);

  const pdfFileRef = useRef(pdfFile);
  const isSharingRef = useRef<boolean>(false);

  const currentName = pdfFile?.download_name || "";
  const ready = pdf && pdfFile && pdf.name === currentName ? pdf : null;
  const isPrinting = !!currentName && printingNames.includes(currentName);
  const viewStatus =
    printView && printView.name === currentName ? printView.status : null;
  const canPrint = mobileDevice
    ? viewStatus === "ready" || viewStatus === "error"
    : !!ready && !isPrinting;
  const canShare = !!ready;
  const isPreparing =
    mobileDevice && viewStatus !== "ready" && viewStatus !== "error";
  const showSpinner = isPrinting || isPreparing;
  const preparingName = printView?.name || "";

  const printHint = () => {
    if (!mobileDevice) {
      return isPrinting
        ? "પ્રિન્ટ તૈયાર થઈ રહી છે..."
        : "આ ઉપકરણની પ્રિન્ટ વિન્ડો ખોલો";
    }

    if (viewStatus === "ready") return "આ ઉપકરણની પ્રિન્ટ વિન્ડો ખોલો";
    if (viewStatus === "error") return "પ્રિન્ટ તૈયાર થઈ નથી, ફરી પ્રયત્ન કરવા દબાવો";

    return "પ્રિન્ટ તૈયાર થઈ રહી છે...";
  };

  useEffect(() => {
    pdfFileRef.current = pdfFile;
  }, [pdfFile]);

  useEffect(() => {
    if (!pdfFile) return undefined;

    let active = true;

    GET("/v1/admin/pdf/file", { name: pdfFile.download_name })
      .then((response: any) => {
        if (!active) return;

        if (response?.data?.status === "success") {
          const content: string = response.data.data.content;
          const binary = atob(content);
          const bytes = new Uint8Array(binary.length);

          for (let index = 0; index < binary.length; index += 1) {
            bytes[index] = binary.charCodeAt(index);
          }

          setPdf({
            name: pdfFile.download_name,
            content,
            file: new File([bytes], pdfFile.download_name, {
              type: "application/pdf",
            }),
          });
        } else {
          toast.error(response?.data?.message || errorMessage, {
            ...(toastStyles as any),
          });
          dispatch(updatePdfFile(null));
        }
      })
      .catch(() => {
        if (!active) return;

        toast.error(errorMessage, { ...(toastStyles as any) });
        dispatch(updatePdfFile(null));
      });

    return () => {
      active = false;
    };
  }, [pdfFile, dispatch]);

  useEffect(() => {
    if (!mobileDevice || !ready || preparingName === ready.name) return;

    const name = ready.name;

    setPrintView({ name, status: "preparing" });

    preparePrintView(name, ready.content)
      .then(() => {
        // A run that a newer one replaced also resolves; only the run whose pages are in place marks it ready.
        setPrintView((current) =>
          current?.name === name && isPrintViewReady(name)
            ? { name, status: "ready" }
            : current
        );
      })
      .catch((error: any) => {
        setPrintView((current) =>
          current?.name === name ? { name, status: "error" } : current
        );
        toast.error(error?.message || errorMessage, {
          ...(toastStyles as any),
        });
      });
  }, [ready, preparingName]);

  useEffect(() => {
    return () => {
      clearPrintView();
      setPrintView(null);
    };
  }, [currentName]);

  useEffect(() => {
    setPdf(null);
    dispatch(updatePdfFile(null));
  }, [pathname, dispatch]);

  const closeModal = () => {
    setPdf(null);
    dispatch(updatePdfFile(null));
  };

  const closeIfCurrent = (name: string) => {
    if (pdfFileRef.current?.download_name === name) closeModal();
  };

  const handlePrint = async () => {
    if (!pdfFile || !canPrint) return;

    const name = pdfFile.download_name;

    if (mobileDevice) {
      if (viewStatus === "error" || !openPrintWindow(name)) {
        setPrintView(null);
      }

      return;
    }

    if (openPrintWindow(name) || !ready) return;

    setPrintingNames((names) => [...names, name]);

    try {
      await preparePrintView(name, ready.content);

      if (pdfFileRef.current?.download_name === name) {
        openPrintWindow(name);
      } else if (isPrintViewReady(name)) {
        clearPrintView();
      }
    } catch (error: any) {
      toast.error(error?.message || errorMessage, {
        ...(toastStyles as any),
      });
    }

    setPrintingNames((names) => names.filter((item) => item !== name));
  };

  const handleShare = () => {
    if (!ready || isSharingRef.current) return;

    const shareData = { files: [ready.file] };

    if (
      typeof navigator.canShare === "function" &&
      navigator.canShare(shareData)
    ) {
      isSharingRef.current = true;

      navigator
        .share(shareData)
        .then(() => closeIfCurrent(ready.name))
        .catch((error: any) => {
          if (error?.name !== "AbortError") {
            toast.error(
              "PDF શેર કરવામાં ભૂલ આવી, કૃપા કરીને ફરીથી પ્રયત્ન કરો.",
              { ...(toastStyles as any) }
            );
          }
        })
        .finally(() => {
          isSharingRef.current = false;
        });

      return;
    }

    const url = URL.createObjectURL(ready.file);
    const link = document.createElement("a");

    link.href = url;
    link.download = ready.name;
    document.body.appendChild(link);
    link.click();
    link.remove();

    setTimeout(() => URL.revokeObjectURL(url), 60000);

    toast.info("આ બ્રાઉઝરમાં શેર ઉપલબ્ધ નથી, PDF ડાઉનલોડ કરવામાં આવી છે.", {
      ...(toastStyles as any),
    });

    closeModal();
  };

  const handleOpen = () => {
    if (!pdfFile) return;

    openDownload(pdfFile.download_link);
  };

  return (
    <Dialog open={pdfFile !== null} onClose={closeModal}>
      <DialogContent size="md" className={sheetClass}>
        <DialogHeader className="max-sm:pr-8 max-sm:text-left">
          <h2 className={dialogTitleClass}>PDF તૈયાર છે</h2>
          <p className="break-all text-sm text-gray-500 max-sm:break-normal max-sm:break-words">
            {pdfFile?.download_name}
          </p>
        </DialogHeader>

        <div className="hidden grid-cols-3 gap-3 sm:grid">
          <button
            type="button"
            className={printTileClass}
            disabled={!canPrint}
            onClick={handlePrint}
          >
            {showSpinner ? (
              <span className="h-7 w-7 animate-spin rounded-full border-2 border-white/30 border-t-white"></span>
            ) : (
              <LuPrinter className="h-7 w-7" />
            )}
            પ્રિન્ટ
          </button>

          <button
            type="button"
            className={outlineTileClass}
            disabled={!canShare}
            onClick={handleShare}
          >
            <LuShare2 className="h-7 w-7" />
            શેર
          </button>

          <button
            type="button"
            className={outlineTileClass}
            onClick={handleOpen}
          >
            <LuExternalLink className="h-7 w-7" />
            ખોલો
          </button>
        </div>

        <div className="grid gap-2 sm:hidden">
          <button
            type="button"
            className={printRowClass}
            disabled={!canPrint}
            onClick={handlePrint}
          >
            <span className={cx(rowIconClass, "bg-white/15")}>
              {showSpinner ? (
                <span className="h-6 w-6 animate-spin rounded-full border-2 border-white/30 border-t-white"></span>
              ) : (
                <LuPrinter className="h-6 w-6" />
              )}
            </span>
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="text-base font-semibold">પ્રિન્ટ</span>
              <span className="text-xs text-white/75">{printHint()}</span>
            </span>
            <LuChevronRight className="h-5 w-5 shrink-0 text-white/60" />
          </button>

          <button
            type="button"
            className={outlineRowClass}
            disabled={!canShare}
            onClick={handleShare}
          >
            <span className={cx(rowIconClass, "bg-[#113F67]/10")}>
              <LuShare2 className="h-6 w-6" />
            </span>
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="text-base font-semibold">શેર</span>
              <span className="text-xs text-gray-500">
                WhatsApp કે બીજી એપમાં મોકલો
              </span>
            </span>
            <LuChevronRight className="h-5 w-5 shrink-0 text-gray-400" />
          </button>

          <button
            type="button"
            className={outlineRowClass}
            onClick={handleOpen}
          >
            <span className={cx(rowIconClass, "bg-[#113F67]/10")}>
              <LuExternalLink className="h-6 w-6" />
            </span>
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="text-base font-semibold">ખોલો</span>
              <span className="text-xs text-gray-500">બ્રાઉઝરમાં PDF જુઓ</span>
            </span>
            <LuChevronRight className="h-5 w-5 shrink-0 text-gray-400" />
          </button>
        </div>

        {!ready && (
          <p className="flex items-center justify-center text-sm text-gray-500">
            <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-[#113F67]/30 border-t-[#113F67]"></span>
            PDF લોડ થઈ રહી છે...
          </p>
        )}

        <button
          type="button"
          className={closeButtonClass}
          onClick={closeModal}
        >
          બંધ કરો
        </button>
      </DialogContent>
    </Dialog>
  );
};

export default PdfActions;
